import { useQueries, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';

import { Box, Button, Flex, Heading, Icon, Image, List, Modal, Tooltip } from '@customerio/pluma-components/react';
import { fireConfetti } from '@/components/feedback/confetti';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useSetupGuide } from '@/features/setup-guide/hook';
import { useTrackEvent } from '@/services/analytics';

import { RocketItem } from './item';
import type { RocketMeta } from './rocket-configs';
import { CONFIGS, ROCKET_STEPS_COMPLETION_MODEL_IDS, getRocketConfigById } from './rocket-configs';
import * as styles from './index.css';
import { RocketHeader } from './header';
import { RocketFooter } from './footer';
import { RocketAdventuresSelector } from './adventures-selector';
import { RocketFloatingButton } from './floating-button';
import { useOpenState } from './hooks/use-open-state';
import { useRocketCompletion } from './hooks/use-rocket-completion';
import { useMatches } from 'react-router-dom';

const CURRENT_STEP_REFRESH_INTERVAL = 5_000;
const OPEN_REFRESH_INTERVAL = 30_000;
const CLOSED_REFRESH_INTERVAL = 60_000;

export const ROCKET_DISMISSED_KEY = 'hydra:rocket-dismissed';

interface RocketProps {
  config?: RocketMeta | null;
  onConfigChange?: (config: RocketMeta | null) => void;
}

const fireConfettiForStep = (stepIndex: number) => {
  // Fire confetti after a small delay, for visual reasons
  setTimeout(() => {
    // TODO: Using a simple query selector for now, should probably be replaced with refs
    fireConfetti(document.querySelector(`.rocket-list-item:nth-child(${stepIndex + 1})`));
  }, 500);
};

export function Rocket({ config, onConfigChange }: RocketProps) {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const matches = useMatches();

  const lastMatch = matches[matches.length - 1];
  const currentPath = lastMatch.params['*'];

  const { id, steps, modal, modelId, onInit, productTourHref } = config ?? {};

  const { setupGuideQuery } = useSetupGuide();
  const { isOpen, openTemporarily, close, open, isPermanentlyOpen, hasUserManuallyClosed } = useOpenState();
  const [currentStep, setCurrentStep] = useState(-1);
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [areAllRocketsCompleted, setAreAllRocketsCompleted] = useState(false);
  const safeNavigate = useSafeNavigate();
  const track = useTrackEvent();
  const { isLoading } = useQuery({
    queryKey: ['rocket-init', id],
    // Use a noop function if onInit is not provided, as react-query v5
    // will throw an error if queryFn is not a function and there's no
    // default query set up.
    queryFn: onInit ?? (() => Promise.resolve()),
    enabled: typeof onInit === 'function',
  });

  const isConfigSelected = id != null;
  const isTourRunning = window.sessionStorage.getItem('cio-tour-is-running') != null;
  let isRocketDismissedLocalStorage = false;
  try {
    isRocketDismissedLocalStorage = localStorage.getItem(ROCKET_DISMISSED_KEY) === 'true';
  } catch {
    // localStorage might fail in some edge cases. Prevent the error from crashing the app.
  }
  const [isRocketDismissed, setIsRocketDismissed] = useState(isRocketDismissedLocalStorage);

  const queries = useQueries({
    queries: (steps ?? []).map((step, index) => {
      let refetchInterval = isOpen ? OPEN_REFRESH_INTERVAL : CLOSED_REFRESH_INTERVAL;
      // Update the "current" step (i.e. the topmost incomplete step) more frequently
      if (currentStep === index) {
        refetchInterval = CURRENT_STEP_REFRESH_INTERVAL;
      }
      if (!isConfigSelected) {
        refetchInterval = 0;
      }

      let isComplete = false;
      if (setupGuideQuery.data?.setup_guide?.[step.modelId] != null) {
        isComplete = true;
        refetchInterval = 0;
      }

      return {
        queryKey: ['rocket', id, step.id, workspaceId],
        // TODO: Error handling
        queryFn: async () => step.queryFn(workspaceId),
        enabled: isConfigSelected && !isComplete,
        refetchInterval,
      };
    }),
  });

  const normalizedSteps = (steps ?? []).map((step, index) => {
    let completion = step.completion(queries[index].data as any);

    // Clamp the completion value to be between 0 and 1
    completion = Math.max(0, Math.min(1, completion));

    // If the step is already marked as completed, set the completion to 1
    if (setupGuideQuery.data?.setup_guide?.[step.modelId] != null) {
      completion = 1;
    }

    // Pass worksapceId and setupGuide to function hrefs
    let href: string | null = (step.href as string) ?? null;
    if (typeof step.href === 'function' && setupGuideQuery.data != null) {
      href = step.href(setupGuideQuery.data.setup_guide, currentPath);
    }

    return {
      ...step,
      completion,
      href,
    };
  });

  // With ReactQuery, isLoading is true even if the query is disabled, so we also re-check the disabled condition
  const isOnInitLoading = isLoading === true && typeof onInit === 'function';

  const isReady =
    isOnInitLoading === false &&
    setupGuideQuery.isLoading === false &&
    queries.every((query) => query.isLoading === false);

  const totalCompletedSteps = normalizedSteps.reduce((acc, step) => acc + (step.completion >= 1 ? 1 : 0), 0);

  useEffect(
    // Once data is ready, check if the Rocket should be initially hidden (i.e. the user has completed all steps).
    // Otherwise, the visibility will be handled elsewhere, to allow displaying completion states.
    function handleInitialVisibility() {
      if (!isConfigSelected && hasUserManuallyClosed === false) {
        // If the Rocket is not configured, we open it by default.
        open();
        return;
      }

      if (!isReady) {
        return;
      }

      // The initial state of the Rocket is closed.
      // However, if the tour is not running and the user has never manually closed it, we open it.
      if (isTourRunning === false && hasUserManuallyClosed === false) {
        open();
      }
    },
    [isReady, isTourRunning, hasUserManuallyClosed, isConfigSelected],
  );

  useEffect(
    // Updates the current step (i.e. the topmost incomplete step), mainly for highlighting
    // and for triggering more frequent query refetches.
    // If set to -1, no steps are considered "current".

    // TODO: This is updated through an effect due to a circular dependency
    // (currentStep -> queries -> normalizedSteps -> currentStep),
    // which requires `currentStep` to be set as a state and not as a regular variable.
    // Should be temporary, part of this dependency should be lifted.

    function updateCurrentStep() {
      if (!isConfigSelected) {
        setCurrentStep(-1);
        return;
      }

      const newCurrentStep = normalizedSteps.findIndex((step) => step.completion < 1);
      if (newCurrentStep !== currentStep) {
        setCurrentStep(newCurrentStep);
      }
    },
    [isConfigSelected, normalizedSteps],
  );

  const handleStepCompleted = ({ rocketId, stepIndex }: { rocketId: string; stepIndex: number }) => {
    const step = normalizedSteps[stepIndex];

    fireConfettiForStep(stepIndex);

    track('Rocket: Step completed', {
      'Step Label': step.ariaLabel,
      rocket: rocketId,
    });

    openTemporarily();
  };

  const handleAllStepsCompleted = ({ id, areAllRocketsCompleted }: { id: string; areAllRocketsCompleted: boolean }) => {
    track('Rocket: All steps completed', { rocket: id });

    if (areAllRocketsCompleted) {
      track('Rocket: All adventures completed', { rocket: id });
      setAreAllRocketsCompleted(true);
    }

    setTimeout(() => {
      if (modal != null) {
        setIsCompletionModalOpen(true);
      }
    }, 1000);
  };

  useRocketCompletion({
    id,
    modelId,
    steps: normalizedSteps,
    isReady,
    onStepCompleted: handleStepCompleted,
    onAllStepsCompleted: handleAllStepsCompleted,
  });

  const handleCloseButtonClick = () => {
    track('Rocket: Manually closed the Rocket panel', { rocket: id });
    close();
  };

  const handleBackButtonClick = () => {
    onConfigChange?.(null);
  };

  const handleCompletionModalClose = () => {
    track('Rocket: Closed the completion modal', { rocket: id });
    setIsCompletionModalOpen(false);
  };

  const handleCompletionModalCTAClick = (event: React.MouseEvent) => {
    track('Rocket: Clicked on the completion modal CTA', { rocket: id });
    handleLinkClick(event);
    handleCompletionModalClose();
  };

  const handleLinkClick = (event: React.MouseEvent) => {
    event.preventDefault();
    safeNavigate(event.currentTarget.getAttribute('href') ?? '');
  };

  const handleRocketButtonClick = () => {
    track('Rocket: Clicked on the Rocket button', { rocket: id });
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  const handleRocketItemClick = (event: React.MouseEvent) => {
    event.preventDefault();
    close();
  };

  const handleRocketConfigClick = (event: React.MouseEvent) => {
    event.preventDefault();
    const configId = event.currentTarget.getAttribute('data-config-id');
    if (configId != null) {
      const config = getRocketConfigById(configId);
      onConfigChange?.(config);
    }
  };

  const shouldDisplayRocketSteps = isConfigSelected && (isReady || normalizedSteps.length > 0);

  useEffect(
    function trackRocketVisibility() {
      if (isOpen) {
        track('Rocket: Viewed the Rocket panel', { rocket: id });
      }
    },
    [isOpen],
  );

  const completedAdventures = ROCKET_STEPS_COMPLETION_MODEL_IDS.filter((key) => {
    return setupGuideQuery.data?.setup_guide?.[key] != null;
  }).length;

  const areAllAdventuresCompleted = completedAdventures === CONFIGS.length;

  const shouldDisplayUpgradeButton = areAllAdventuresCompleted && !isConfigSelected;

  const handleDismiss = () => {
    track('Rocket: Dismissed the Rocket footer');
    try {
      localStorage.setItem(ROCKET_DISMISSED_KEY, 'true');
    } catch {
      // localStorage might fail in some edge cases. Prevent the error from crashing the app.
    }
    setIsRocketDismissed(true);
    close();
  };

  if (isRocketDismissed) {
    return null;
  }

  return (
    <Fragment>
      <Box className={styles.container}>
        <RocketFloatingButton
          onClick={handleRocketButtonClick}
          hasConfig={isConfigSelected}
          completedSteps={totalCompletedSteps}
          totalSteps={normalizedSteps.length}
        />

        <Box
          className={clsx(styles.contentContainer, {
            [styles.contentContainerOpen]: isOpen && isReady,
          })}
          aria-hidden={!isPermanentlyOpen}
          data-tourtip-target="rocket-content"
        >
          <Flex as="article" direction="column" alignItems="stretch">
            <RocketHeader
              config={config ?? null}
              completedSteps={totalCompletedSteps}
              onClose={handleCloseButtonClick}
              onBack={handleBackButtonClick}
            />

            {shouldDisplayRocketSteps ? (
              <List styleType="none" as="menu">
                {normalizedSteps.map((step, index) => (
                  <RocketItem
                    key={`${config?.id}-${index}`}
                    completion={step.completion}
                    highlight={currentStep === index}
                    href={step.href}
                    ariaLabel={step.ariaLabel}
                    onClick={handleRocketItemClick}
                  >
                    {step.content}
                    {step.tooltip != null && step.completion < 1 ? (
                      <Tooltip content={step.tooltip} placement="right">
                        <Icon name="info" size="sm" color="accent" ml="050" />
                      </Tooltip>
                    ) : null}
                  </RocketItem>
                ))}
              </List>
            ) : (
              <RocketAdventuresSelector
                setupGuide={setupGuideQuery.data?.setup_guide ?? null}
                onConfigClick={handleRocketConfigClick}
              />
            )}
            <RocketFooter
              productTourHref={productTourHref}
              workspaceId={workspaceId}
              shouldDisplayUpgradeButton={shouldDisplayUpgradeButton}
              onDismiss={handleDismiss}
            />
          </Flex>
        </Box>
      </Box>

      {modal != null ? (
        <Modal isOpen={isCompletionModalOpen} onClose={handleCompletionModalClose}>
          <Flex direction="column" alignItems="stretch" rowGap="400" p="300" pt="0">
            {areAllRocketsCompleted ? (
              <Fragment>
                <Heading level="1" textAlign="center" className={styles.completionModalHeading}>
                  All adventures complete! Continue exploring our sample workspace or upgrade to learn more.
                </Heading>
                <Flex columnGap="200" justifyContent="center">
                  <Button href="/settings/billing/available-plans/journeys" onClick={handleLinkClick}>
                    Upgrade
                  </Button>
                  <Button href="/sample-workspace" variant="primary" onClick={handleCompletionModalCTAClick}>
                    View sample workspace
                  </Button>
                </Flex>
              </Fragment>
            ) : (
              <Fragment>
                <Heading level="1" textAlign="center" className={styles.completionModalHeading}>
                  {modal.text}
                </Heading>
                <Image src={modal.image} />
                <Flex columnGap="200" justifyContent="center">
                  <Button onClick={handleCompletionModalClose}>Close</Button>
                  <Button href={modal.href(workspaceId)} variant="primary" onClick={handleCompletionModalCTAClick}>
                    Continue to the next adventure
                  </Button>
                </Flex>
              </Fragment>
            )}
          </Flex>
        </Modal>
      ) : null}
    </Fragment>
  );
}
